import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';
import { getElementById } from '@shared/helpers/recursive-element.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import type {
  IElementExpressionProperty,
  IElementProperty,
} from '@shared/models/element-property.model';
import {
  elementQuantityUnitRecord,
  type ElementQuantityExpressionName,
  type ElementQuantityName,
} from '@shared/models/element-quantities.model';
import type { Baseline } from '@shared/models/project.model';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { ELEMENT_PERCENT_PROPERTY_SCALE_MAX } from './percent-element-property-display.helpers';

/** Human-readable value for proposal/action rows when an expression was edited. */
export const FORMULA_UPDATED_CHANGE_DISPLAY_VALUE = 'Formula updated';

/**
 * Converts stored numeric values to a 0–100 display scale before appending `%`.
 * When element metadata is unavailable, infer fraction vs percent scale from
 * {@link ELEMENT_PERCENT_PROPERTY_SCALE_MAX} and quantity `%` units.
 * Templates store fractions when `max <= 1` or `max === 100` (see expression validation).
 */
export const scaleStoredNumberToDisplayPercent = (
  value: number,
  max?: number,
): number => {
  const treatAsFraction =
    max !== undefined ? max <= 1 || max === 100 : Math.abs(value) <= 1;
  return treatAsFraction ? value * 100 : value;
};

const isPercentExpressionProperty = (
  prop: IElementProperty | undefined,
): prop is IElementExpressionProperty =>
  prop !== undefined && 'unit' in prop && prop.unit === '%';

/** True when {@link formatChangeNumericDisplayString} applies `%` for this property. */
export const isPercentPropertyForChangeDisplay = (
  propertyName: string,
  elementId: string,
  version?: Baseline,
): boolean => {
  if (ELEMENT_PERCENT_PROPERTY_SCALE_MAX[propertyName] !== undefined)
    return true;
  const raw =
    version !== undefined
      ? getElementById(version, elementId, false)
      : undefined;
  const element =
    raw !== undefined && projectGuards.is.element(raw) ? raw : undefined;
  const prop = element?.properties?.find((p) => p.name === propertyName);
  return isPercentExpressionProperty(prop);
};

/**
 * Reads a numeric scale suitable for {@link formatChangeNumericDisplayString} from
 * `count` / `fallbackCount` payloads (number, expression literal, or {@link ExpressionValue}).
 */
export const resolveNumericFromPropertyCountPayload = (
  payload: unknown,
): number | undefined => {
  if (!guards.is.defined(payload)) return undefined;
  if (guards.is.boolean(payload)) return undefined;
  if (guards.is.number(payload))
    return guards.is.finiteNumber(payload) ? payload : undefined;
  if (guards.is.string(payload)) {
    const n = parseFloat(payload.trim().replace(',', '.'));
    return guards.is.finiteNumber(n) ? n : undefined;
  }
  if (isExpressionValue(payload)) {
    if (payload.value !== undefined && guards.is.finiteNumber(payload.value))
      return payload.value;
    if (guards.is.finiteNumber(payload.resolved)) return payload.resolved;
    const fromFormatted = parseFloat(
      payload.formatted.replace(/\s/g, '').replace(',', '.'),
    );
    return guards.is.finiteNumber(fromFormatted) ? fromFormatted : undefined;
  }
  return undefined;
};

type FormatChangeNumericArgs = {
  kind: 'property' | 'quantity';
  name: string;
  value: number;
  elementId: string;
  version?: Baseline;
};

/**
 * Formats a resolved numeric project-change payload for lists and summaries.
 * Percent-unit properties and quantities show values like `56%` instead of `0.56`.
 */
export const formatChangeNumericDisplayString = ({
  kind,
  name,
  value,
  version,
  elementId,
}: FormatChangeNumericArgs): string => {
  const raw =
    version !== undefined
      ? getElementById(version, elementId, false)
      : undefined;
  const element =
    raw !== undefined && projectGuards.is.element(raw) ? raw : undefined;

  if (kind === 'property') {
    const prop = element?.properties?.find((p) => p.name === name);
    if (isPercentExpressionProperty(prop)) {
      const percent = scaleStoredNumberToDisplayPercent(value, prop.max);
      return `${getDisplayNumber(percent)}%`;
    }
    const fallbackMax = ELEMENT_PERCENT_PROPERTY_SCALE_MAX[name];
    if (fallbackMax !== undefined) {
      const percent = scaleStoredNumberToDisplayPercent(value, fallbackMax);
      return `${getDisplayNumber(percent)}%`;
    }
    return String(value);
  }

  const qtyKey = name as ElementQuantityName;
  const unit =
    elementQuantityUnitRecord[qtyKey as ElementQuantityExpressionName];
  if (unit === '%') {
    const qtyRecord = element?.quantity;
    const qty =
      qtyRecord != null
        ? qtyRecord[qtyKey as keyof typeof qtyRecord]
        : undefined;
    const max =
      guards.is.object(qty) &&
      guards.is.keyOf(qty, 'max') &&
      guards.is.finiteNumber(qty.max)
        ? qty.max
        : undefined;
    const percent = scaleStoredNumberToDisplayPercent(value, max);
    return `${getDisplayNumber(percent)}%`;
  }

  return String(value);
};

/**
 * Formats `element.property.formatted` when the property uses `%` (otherwise returns input).
 */
export const formatPropertyFormattedDisplayValue = (
  formattedRaw: string,
  propertyName: string,
  elementId: string,
  version?: Baseline,
): string => {
  if (!isPercentPropertyForChangeDisplay(propertyName, elementId, version))
    return formattedRaw;
  const parsed = parseFloat(formattedRaw.replace(/\s/g, '').replace(',', '.'));
  if (!guards.is.finiteNumber(parsed)) return formattedRaw;
  return formatChangeNumericDisplayString({
    kind: 'property',
    name: propertyName,
    value: parsed,
    elementId,
    version,
  });
};

type FormatPropertyCountPayloadArgs = {
  propertyName: string;
  elementId: string;
  version?: Baseline;
  payload: unknown;
};

/**
 * True when a count/fallbackCount payload is a numeric edit that should show a
 * separate unit column (not a formula edit or other non-numeric display).
 */
export const countChangePayloadShowsNumericUnit = (
  payload: unknown,
): boolean => {
  if (isExpressionValue(payload) && payload.expression.trim().length > 0) {
    return false;
  }
  return resolveNumericFromPropertyCountPayload(payload) !== undefined;
};

/**
 * Display string for `element.property.fallbackCount` / `count` on proposal action rows.
 */
export const formatElementPropertyCountChangeDisplay = ({
  propertyName,
  elementId,
  version,
  payload,
}: FormatPropertyCountPayloadArgs): string => {
  if (isExpressionValue(payload) && payload.expression.trim().length > 0) {
    return FORMULA_UPDATED_CHANGE_DISPLAY_VALUE;
  }
  const n = resolveNumericFromPropertyCountPayload(payload);
  if (n !== undefined) {
    return formatChangeNumericDisplayString({
      kind: 'property',
      name: propertyName,
      value: n,
      elementId,
      version,
    });
  }
  if (isExpressionValue(payload)) {
    if (payload.formatted.length > 0)
      return formatPropertyFormattedDisplayValue(
        payload.formatted,
        propertyName,
        elementId,
        version,
      );
  }
  if (guards.is.string(payload)) return payload;
  return '';
};

/**
 * Formats quantity `formatted` when that quantity uses unit `%` (otherwise returns input).
 */
export const formatQuantityFormattedDisplayValue = (
  formattedRaw: string,
  quantityName: string,
  elementId: string,
  version?: Baseline,
): string => {
  const qtyKey = quantityName as ElementQuantityName;
  const unit =
    elementQuantityUnitRecord[qtyKey as ElementQuantityExpressionName];
  if (unit !== '%') return formattedRaw;
  const parsed = parseFloat(formattedRaw.replace(/\s/g, '').replace(',', '.'));
  if (!guards.is.finiteNumber(parsed)) return formattedRaw;
  return formatChangeNumericDisplayString({
    kind: 'quantity',
    name: quantityName,
    value: parsed,
    elementId,
    version,
  });
};
